import { Boundaries } from "@planzone/core";
import {
  Identifiable,
  Resource,
  ResourcesCounts,
  resourcesCounts,
  ResourceTypes,
} from "@planzone/dto";
import { events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = {
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
  counts: resourcesCounts,
};

const typeCounts: { [type in ResourceTypes]: keyof ResourcesCounts } = {
  [ResourceTypes.HUMAN]: "human",
  [ResourceTypes.MATERIAL]: "material",
  [ResourceTypes.VIRTUAL]: "virtual",
};

function handleRequest(state: State): State {
  return { ...state, request: setRequestStateToPending() };
}

function handleSuccess(
  state: State,
  { counts }: Boundaries["retrievePlanzoneResourcesCounts"]["success"],
): State {
  return { counts, request: setRequestStateToSuccess() };
}

function handleFailure(state: State): State {
  return { ...state, request: setRequestStateToFailure() };
}

// Counts of the added (step 1) or removed (step -1) resources, never below zero.
function countResources(
  state: State,
  resources: Identifiable<Resource>[],
  step: 1 | -1,
): State {
  return {
    ...state,
    counts: resources.reduce((counts, { type, deactivated }) => {
      const key = typeCounts[type];

      return {
        ...counts,
        ...(key && { [key]: Math.max(0, counts[key] + step) }),
        inactive: deactivated
          ? Math.max(0, counts.inactive + step)
          : counts.inactive,
      };
    }, state.counts),
  };
}

function handleResourceAddition(
  state: State,
  { resource }: Boundaries["addPlanzoneMaterialResource"]["success"],
): State {
  return countResources(state, [resource], 1);
}

function handleProjectResourceAddition(
  state: State,
  projectResource: Boundaries["addProjectMaterialResource"]["success"],
): State {
  return countResources(state, [projectResource.resource], 1);
}

function handleInvitationsSuccess(
  state: State,
  { members }: Boundaries["inviteMembers"]["success"],
): State {
  return countResources(
    state,
    members.map(({ resource }) => resource),
    1,
  );
}

function handleInvitationsFailure(
  state: State,
  { succeededInvitations }: Boundaries["inviteMembers"]["failure"],
): State {
  return countResources(
    state,
    succeededInvitations.map(({ resource }) => resource),
    1,
  );
}

function handleResourceRemoval(
  state: State,
  { resource }: Boundaries["removeResourceFromPlanzone"]["success"],
): State {
  return countResources(state, [resource], -1);
}

function handleResourceDisabling(state: State): State {
  return {
    ...state,
    counts: { ...state.counts, inactive: state.counts.inactive + 1 },
  };
}

function handleResourceActivation(state: State): State {
  return {
    ...state,
    counts: {
      ...state.counts,
      inactive: Math.max(0, state.counts.inactive - 1),
    },
  };
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrievePlanzoneResourcesCountsRequested]: handleRequest,
    [events.retrievePlanzoneResourcesCountsSucceeded]: handleSuccess,
    [events.retrievePlanzoneResourcesCountsFailed]: handleFailure,
    [events.addPlanzoneMaterialResourceSucceeded]: handleResourceAddition,
    [events.addPlanzoneVirtualResourceSucceeded]: handleResourceAddition,
    [events.addProjectMaterialResourceSucceeded]: handleProjectResourceAddition,
    [events.addProjectVirtualResourceSucceeded]: handleProjectResourceAddition,
    [events.inviteMembersSucceeded]: handleInvitationsSuccess,
    [events.inviteMembersFailed]: handleInvitationsFailure,
    [events.removeResourceFromPlanzoneSucceeded]: handleResourceRemoval,
    [events.disableResourceSucceeded]: handleResourceDisabling,
    [events.activateResourceSucceeded]: handleResourceActivation,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
